import { useState, useEffect, useCallback, useRef } from "react";
import { getConversations, getMessages, sendMessage, markSeen } from "../APIs/chatApi";

const getWsBase = () => {
  const api = process.env.REACT_APP_API_URL || "http://localhost:8000";
  return api.replace("https://", "wss://").replace("http://", "ws://");
};

const useChat = (openConvo = null) => {
  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  const scrollRef = useRef(null);
  const initializedRef = useRef(false);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const activeConvoRef = useRef(null);

  // Keep ref in sync for use inside WS closure
  useEffect(() => { activeConvoRef.current = activeConvo; }, [activeConvo]);

  // ── Conversations load ──────────────────────────────────────────────────────
  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await getConversations();
        setConversations(data);
        if (openConvo && !initializedRef.current) {
          initializedRef.current = true;
          const existing = data.find((c) => c.id === openConvo.id);
          if (existing) {
            setActiveConvo(existing);
          } else {
            setConversations([openConvo, ...data]);
            setActiveConvo(openConvo);
          }
        }
      } catch (err) {
        console.error("Conversations fetch failed:", err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [openConvo]);

  // ── Messages load when convo changes ──────────────────────────────────────
  useEffect(() => {
    if (!activeConvo) return;
    setMessages([]);
    setIsOnline(false);
    setIsTyping(false);

    const fetch = async () => {
      try {
        const data = await getMessages(activeConvo.id);
        setMessages(data);
        if (data.length > 0) {
          markSeen(data[data.length - 1].id).catch(() => {});
        }
      } catch (err) {
        console.error("Messages fetch failed:", err);
      }
    };
    fetch();
  }, [activeConvo]);

  // ── WebSocket connection ───────────────────────────────────────────────────
  useEffect(() => {
    if (!activeConvo) return;

    const token = localStorage.getItem("access_token");
    if (!token) return;

    let ws;
    try {
      ws = new WebSocket(`${getWsBase()}/ws/chat/${activeConvo.id}/?token=${token}`);
      wsRef.current = ws;
    } catch {
      return;
    }

    ws.onmessage = (e) => {
      const data = JSON.parse(e.data);
      const otherUsername = activeConvoRef.current?.other_user?.username;

      if (data.type === "chat_message") {
        if (data.sender === otherUsername && data.message) {
          setMessages((prev) => {
            if (prev.some((m) => String(m.id) === String(data.message.id))) return prev;
            return [...prev, { ...data.message, fromMe: false }];
          });
          setTimeout(() => {
            if (scrollRef.current)
              scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
          }, 50);
        }
      } else if (data.type === "typing") {
        if (data.user === otherUsername) {
          setIsTyping(true);
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 2500);
        }
      } else if (data.type === "user_online") {
        if (data.user === otherUsername) setIsOnline(true);
      } else if (data.type === "user_offline") {
        if (data.user === otherUsername) setIsOnline(false);
      }
    };

    ws.onclose = () => {
      setIsOnline(false);
    };

    return () => {
      ws.close();
      wsRef.current = null;
      setIsOnline(false);
      setIsTyping(false);
      clearTimeout(typingTimeoutRef.current);
    };
  }, [activeConvo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-scroll ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // ── Send typing event (debounced — caller throttles) ──────────────────────
  const sendTyping = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ event: "typing" }));
    }
  }, []);

  // ── Send message ───────────────────────────────────────────────────────────
  const handleSend = useCallback(
    async (sharedPostId = null) => {
      if (!text.trim() && !sharedPostId) return;
      if (!activeConvo || sending) return;

      const msgText = text.trim();
      setText("");
      setSending(true);

      const tempMsg = {
        id: `temp-${Date.now()}`,
        text: msgText,
        fromMe: true,
        created_at: new Date().toISOString(),
        shared_post: null,
      };
      setMessages((prev) => [...prev, tempMsg]);

      try {
        const real = await sendMessage(activeConvo.id, msgText, sharedPostId);
        setMessages((prev) => prev.map((m) => (m.id === tempMsg.id ? real : m)));

        // Relay saved message to the other user via WebSocket
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(
            JSON.stringify({ event: "message", message: { ...real, fromMe: false } })
          );
        }
      } catch {
        setMessages((prev) => prev.filter((m) => m.id !== tempMsg.id));
        setText(msgText);
      } finally {
        setSending(false);
      }
    },
    [text, activeConvo, sending]
  );

  const handleMessageDeleted = (messageId) => {
    setMessages((prev) => prev.filter((m) => m.id !== messageId));
  };

  return {
    conversations, activeConvo, messages,
    text, loading, sending, scrollRef,
    isOnline, isTyping,
    setText, setActiveConvo, handleSend, handleMessageDeleted, sendTyping,
  };
};

export default useChat;
